/**
 * State initialization helpers for CardBody component
 * Contains functions to initialize edit state from group/row data
 */

import TimestampConverter from "../helpers/TimestampConverter";
import { convertTo24HourFormatback } from "../services/timeFormat";
import { formatTime } from "../services/trackerServices";
import { getEntryZone, entryDateEditValue } from "../../../../../utils/tenantDate";

/**
 * Initialize edit state from a group or row object
 * This pattern is repeated many times throughout CardBody
 */
export const initializeEditStateFromGroup = ({
  group,
  effectiveSetEditedId,
  setProjectEditDropdownOpen,
  setTagEditDropdownOpen,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setTagIdEdit,
  setTagEdit,
  setTagQuery,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  globalConfig,
  useMomentFormat = false,
}) => {
  effectiveSetEditedId(group._id);
  closeAllDropdowns(setProjectEditDropdownOpen, setTagEditDropdownOpen);
  setTaskNameEdit(group.task);
  setProjectIdEdit(group.project._id);
  setProjectNameEdit(group.project.project_name);
  
  if (group.sprint) {
    setSprintNameEdit(group.sprint.sprint_name);
    setSprintIdEdit(group.sprint._id);
  } else {
    setSprintIdEdit("");
    setSprintNameEdit("");
  }
  
  // Render the entry in the zone it was LOGGED in, not the tenant's. The entry
  // is grouped under its stored `localDate`, so taking its clock time from a
  // different zone put the time and the heading on two different days — a
  // 7:26 PM Oct 8 Pago Pago entry read "11:56 AM" under an Oct 8 heading.
  const entryZone = getEntryZone(group);
  setDateEdit(entryDateEditValue(group, entryZone));
  
  setTagIdEdit(group.tag && group.tag._id);
  setTagEdit(group.tag && group.tag.tag_name);
  setTagQuery("");
  
  setStartTimeEdit(TimestampConverter(group.startTime, entryZone));
  setEndTimeEdit(TimestampConverter(group.endTime, entryZone));
  setStartTimeTemp(TimestampConverter(group.startTime, entryZone));
  setEndTimeTemp(TimestampConverter(group.endTime, entryZone));
  setStartTimeEdit24(convertTo24HourFormatback(TimestampConverter(group.startTime, entryZone)));
  setEndTimeEdit24(convertTo24HourFormatback(TimestampConverter(group.endTime, entryZone)));
  
  setClientEdit(group.client);
  setClientIdEdit(group.clientId);
  setBillableEdit(group.billable);
  
  // Handle both group (with totalDuration) and row (with duration) objects
  const duration = group.totalDuration || group.duration || 0;
  setUFormatedTime(formatDurationFromSeconds(duration));
  setUDuration(duration);
  clearTimeErrors(setUStartTimeError, setUEndTimeError, setUFormatedTimeError);
};

/**
 * Format duration from seconds to HH:MM:SS format
 * @param {number} totalSeconds - Total seconds to format
 * @returns {string} Formatted time string (HH:MM:SS)
 */
export const formatDurationFromSeconds = (totalSeconds) => {
  const duration = totalSeconds || 0;
  return `${formatTime(Math.floor(duration / 3600))}:${formatTime(
    Math.floor(parseInt((duration % 3600) / 60))
  )}:${formatTime(Math.floor(duration % 60))}`;
};

/**
 * Clear all time-related error states
 * @param {Function} setUStartTimeError - Setter for start time error
 * @param {Function} setUEndTimeError - Setter for end time error
 * @param {Function} setUFormatedTimeError - Setter for formatted time error
 */
export const clearTimeErrors = (setUStartTimeError, setUEndTimeError, setUFormatedTimeError) => {
  setUStartTimeError(false);
  setUEndTimeError(false);
  setUFormatedTimeError(false);
};

/**
 * Close all dropdown menus
 * @param {Function} setProjectEditDropdownOpen - Setter for project dropdown
 * @param {Function} setTagEditDropdownOpen - Setter for tag dropdown
 */
export const closeAllDropdowns = (setProjectEditDropdownOpen, setTagEditDropdownOpen) => {
  setProjectEditDropdownOpen(false);
  setTagEditDropdownOpen(false);
};
